import { defineStore } from "pinia";
import { useAuthStore } from "./authStore";
import { useModuleStore } from "./moduleStore";

export const useNotificationStore = defineStore("notification", () => {
  const initialized = ref(false);
  const initFailed = ref(false);
  const syncFailureLogged = ref(false);
  let updatePromise;
  const moduleStore = useModuleStore();
  const authStore = useAuthStore();

  /**
   * @param {(OneSignal: any) => any} func
   * @param {?number} timeoutInMilliseconds
   * @param {?boolean} first
   * @returns {Promise}
   */
  function _queue(func, timeoutInMilliseconds = 20000, first = undefined) {
    if (initFailed.value) {
      return Promise.reject("not initialized");
    }
    return Promise.race([
      new Promise((resolve, reject) => {
        if (window.cordova?.platformId) {
          document.addEventListener(
            "deviceready",
            function () {
              if (!window["plugins"]?.OneSignal) return reject("not initialized");
              let result;
              try {
                result = func(window["plugins"].OneSignal);
              } catch (error) {
                return reject(error);
              }
              if (result instanceof Promise) {
                result.then(resolve).catch(reject);
              } else {
                resolve(result);
              }
            },
            first
          );
        } else {
          if (!window["OneSignalDeferred"]) return reject("not initialized");
          window["OneSignalDeferred"].push(function (OneSignal) {
            let result;
            try {
              result = func(OneSignal);
            } catch (error) {
              return reject(error);
            }
            if (result instanceof Promise) {
              result.then(resolve).catch(reject);
            } else {
              resolve(result);
            }
          });
        }
      }),
      new Promise(function (resolve, reject) {
        setTimeout(function () {
          reject("timeout");
        }, timeoutInMilliseconds);
      }),
    ]);
  }

  // updateTags() now runs on every authenticated start, so an unguarded console.error would send one Sentry event
  // per user per launch — tens of thousands a day, enough to bury everything else. One per session is plenty.
  /**
   * @param {string} message
   */
  function _reportSyncFailure(message) {
    if (syncFailureLogged.value) return;
    syncFailureLogged.value = true;
    console.error(message);
  }

  /**
   * @param {() => Promise<unknown>} check
   * @param {number} timeoutInMilliseconds
   * @returns {Promise<boolean>}
   */
  async function _waitUntil(check, timeoutInMilliseconds = 8000) {
    const deadline = new Date().getTime() + timeoutInMilliseconds;
    for (;;) {
      if (await check()) return true;
      if (new Date().getTime() > deadline) return false;
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  /**
   * @param {*} options
   * @returns {Promise<void>}
   */
  async function init(options) {
    if (initialized.value || initFailed.value) return;
    try {
      if (window.cordova?.platformId) {
        await _queue(
          async function (OneSignal) {
            OneSignal.initialize(options.appId);
            OneSignal.InAppMessages.addEventListener("click", (data) => {
              if (data.result.url) {
                if (/^(alvago:\/?\/)\//.test(data.result.url)) {
                  options.router.push(data.result.url.replace(/^(alvago:\/?\/)\//, "/")).catch(() => {});
                } else if (/^[a-zA-Z]+:\/\/(www\.)?(go|event)(\.[a-z]+)?\.alvarum\.com(\/|$)/.test(data.result.url)) {
                  options.router.push("/" + data.result.url.split("/").slice(3).join("/")).catch(() => {});
                } else {
                  window.open(data.result.url);
                }
              }
              return true;
            });
            OneSignal.Notifications.addEventListener("click", (data) => {
              console.log("OneSignal notification click", JSON.stringify(data.result));
              if (data.result.url) {
                if (/^(alvago:\/?\/)\//.test(data.result.url)) {
                  options.router.push(data.result.url.replace(/^(alvago:\/?\/)\//, "/")).catch(() => {});
                } else if (/^[a-zA-Z]+:\/\/(www\.)?(go|event)(\.[a-z]+)?\.alvarum\.com(\/|$)/.test(data.result.url)) {
                  options.router.push("/" + data.result.url.split("/").slice(3).join("/")).catch(() => {});
                } else {
                  window.open(data.result.url);
                }
              }
              return true;
            });
          },
          60000,
          true
        );
      } else if (location.protocol === "https:") {
        window["OneSignalDeferred"] = window["OneSignalDeferred"] || [];
        const script = document.createElement("script");
        script.id = "onesignal-sdk";
        script.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
        script.defer = true;
        document.head.appendChild(script);
        await _queue(async (OneSignal) => {
          await OneSignal.init(options);
        }, 60000);
      } else {
        initFailed.value = true;
      }
      initialized.value = true;
    } catch (err) {
      console.error("Failed to init OneSignal", err);
      // Never latch initFailed on native: _queue() short-circuits every later call once it is set, so one
      // transient deviceready or plugin timing miss would kill prompt() and updateTags() for the whole session.
      // Leaving both flags false lets the next User.layout mount retry.
      if (!window.cordova?.platformId) initFailed.value = true;
    }
  }

  async function prompt() {
    moduleStore.setNotificationsEnabled(true);
    try {
      return await _queue(async function (OneSignal) {
        const accepted = await OneSignal.Notifications.requestPermission(true);
        moduleStore.setNotificationsEnabled(accepted !== false);
        if (accepted !== false) {
          if (typeof OneSignal.User.PushSubscription !== "undefined") {
            await OneSignal.User.PushSubscription.optIn();
          } else {
            // old API (used in Cordova)
            OneSignal.User.pushSubscription.optIn();
          }
        }
        return accepted !== false;
      });
    } catch (error) {
      console.error(error);
      moduleStore.setNotificationsEnabled(false);
      return false;
    }
  }

  async function logout() {
    try {
      await _queue(async function (OneSignal) {
        await OneSignal.logout();
      });
    } catch (err) {
      console.log("Failed to logout from onesignal", err);
    }
  }

  async function unsubscribe() {
    try {
      await _queue(async function (OneSignal) {
        if (typeof OneSignal.User.PushSubscription !== "undefined") {
          await OneSignal.User.PushSubscription.optOut();
        } else {
          // old API (used in Cordova)
          OneSignal.User.pushSubscription.optOut();
        }
      });
    } catch (err) {
      console.error("Failed to unsubscribe from onesignal", err);
    }
  }

  function updateTags() {
    updatePromise ??= _updateTags().finally(() => (updatePromise = undefined));
    return updatePromise;
  }

  async function _updateTags() {
    try {
      const user = authStore.me;
      if (!user) return _reportSyncFailure("Skipped onesignal identity: no authenticated member");
      const isNative = !!window.cordova?.platformId;
      const externalId = String(user.registration_number || user.id);
      // OneSignal rejects a whole add/update request once a user reaches its per-user tag cap — 2 on the Free plan,
      // raised to 6 on 2026-09-01 — so tags go out in batches by decreasing criticality instead of one shot, and
      // a cap hit only costs the tail. Batch 1 is what news_cron.js filters every send on: losing it makes the
      // member unreachable, and holding it to two keys keeps it landing even against a cap of 2. Batch 2 only
      // keeps the dashboard segmentable by hand, uuids alone are unusable there.
      const batches = [
        { event: user.event_event.name, organization_id: user.organization_organization?.id },
        { organization: user.organization_organization?.name, team: user.team_team?.name },
      ].map((batch) => Object.fromEntries(Object.entries(batch).filter(([, value]) => value !== null && value !== undefined)));
      await _queue(async function (OneSignal) {
        // Tags belong to the User, not to the Subscription, and login() moves the subscription to another User:
        // per OneSignal, properties tied to the previous User "will no longer apply". So anything written before
        // the transfer takes effect stays behind on the anonymous user. getOnesignalId() is not a usable barrier
        // here — the anonymous user already has one — only the external id proves the transfer happened.
        if (isNative && !(await _waitUntil(() => OneSignal.User.pushSubscription.getIdAsync()))) {
          return _reportSyncFailure("Failed to update tags on onesignal: device has no push subscription");
        }
        OneSignal.login(externalId);
        if (isNative && !(await _waitUntil(async () => (await OneSignal.User.getExternalId()) === externalId))) {
          return _reportSyncFailure("Failed to update tags on onesignal: login to " + externalId + " did not take effect");
        }
        // email/phone are aliases, not tags: they are identifiers, they stay out of the per-user tag quota, and
        // unlike addEmail()/addSms() they open no messaging channel — the participant only consented to push.
        // Sent one by one because alias values are unique per label app-wide, so two members sharing an address
        // would otherwise lose both in the same rejected request.
        if (user.email) OneSignal.User.addAlias("email", user.email);
        if (user.phone_number) OneSignal.User.addAlias("phone", user.phone_number);
        for (const [index, batch] of batches.entries()) {
          if (!Object.keys(batch).length) continue;
          // Let the native operation queue flush in between, otherwise both batches merge back into one request.
          if (index) await new Promise((resolve) => setTimeout(resolve, 1500));
          OneSignal.User.addTags(batch);
        }
        if (isNative) {
          const applied = await OneSignal.User.getTags();
          const missing = batches.flatMap((batch) => Object.keys(batch)).filter((key) => !(key in applied));
          if (missing.length) _reportSyncFailure("Onesignal did not keep tags: " + missing.join(", "));
        }
      }, 60000);
    } catch (err) {
      _reportSyncFailure("Failed to update tags on onesignal: " + err);
    }
  }

  return {
    initialized,
    initFailed,
    init,
    prompt,
    logout,
    unsubscribe,
    updateTags,
  };
});
